import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { PageLoader } from '@/components/shared/PageLoader'

// ─── Auth ─────────────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'))
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute'
import { GuestRoute } from '@/features/auth/components/GuestRoute'

// ─── Layout ───────────────────────────────────────────────────────────────────
const AppLayout = lazy(() => import('@/layouts/AppLayout'))

// ─── Dashboard ────────────────────────────────────────────────────────────────
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'))

// ─── Maintenance ──────────────────────────────────────────────────────────────
const WorkOrderListPage = lazy(() => import('@/features/maintenance/pages/WorkOrderListPage'))
const WorkOrderDetailPage = lazy(() => import('@/features/maintenance/pages/WorkOrderDetailPage'))
const WorkOrderFormPage = lazy(() => import('@/features/maintenance/pages/WorkOrderFormPage'))
const PMPlansPage = lazy(() => import('@/features/maintenance/pages/PMPlansPage'))
const PMPlanDetailPage = lazy(() => import('@/features/maintenance/pages/PMPlanDetailPage'))
const PMPlanFormPage = lazy(() => import('@/features/maintenance/pages/PMPlanFormPage'))
const CalendarPage = lazy(() => import('@/features/maintenance/pages/CalendarPage'))
const MaintenanceRequestQueuePage = lazy(() => import('@/features/maintenance/pages/MaintenanceRequestQueuePage'))

// ─── Inventory ────────────────────────────────────────────────────────────────
const InventoryDashboardPage = lazy(() => import('@/features/inventory/pages/InventoryDashboardPage'))
const ItemsListPage = lazy(() => import('@/features/inventory/pages/ItemsListPage'))
const ItemDetailPage = lazy(() => import('@/features/inventory/pages/ItemDetailPage'))
const ItemFormPage = lazy(() => import('@/features/inventory/pages/ItemFormPage'))
const StockPage = lazy(() => import('@/features/inventory/pages/StockPage'))
const StockMovementPage = lazy(() => import('@/features/inventory/pages/StockMovementPage'))
const LocationsPage = lazy(() => import('@/features/inventory/pages/LocationsPage'))
const SpareIssuePage = lazy(() => import('@/features/inventory/pages/SpareIssuePage'))

// ─── Purchase Orders ──────────────────────────────────────────────────────────
const POListPage = lazy(() => import('@/features/purchase-orders/pages/POListPage'))
const PODetailPage = lazy(() => import('@/features/purchase-orders/pages/PODetailPage'))
const POFormPage = lazy(() => import('@/features/purchase-orders/pages/POFormPage'))
const RequestQueuePage = lazy(() => import('@/features/purchase-orders/pages/RequestQueuePage'))
const RFQListPage = lazy(() => import('@/features/purchase-orders/pages/RFQListPage'))

// ─── Vendors ──────────────────────────────────────────────────────────────────
const VendorListPage = lazy(() => import('@/features/vendors/pages/VendorListPage'))
const VendorDetailPage = lazy(() => import('@/features/vendors/pages/VendorDetailPage'))
const VendorFormPage = lazy(() => import('@/features/vendors/pages/VendorFormPage'))

// ─── Reports & Settings ───────────────────────────────────────────────────────
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'))
const EquipmentStatusReportPage = lazy(() => import('@/features/reports/pages/EquipmentStatusReportPage'))
const MaintenanceCostReportPage = lazy(() => import('@/features/reports/pages/MaintenanceCostReportPage'))
const WorkOrderSummaryPage = lazy(() => import('@/features/reports/pages/WorkOrderSummaryPage'))
const PurchaseCostSummaryPage = lazy(() => import('@/features/reports/pages/PurchaseCostSummaryPage'))
const SparesIssuedReportPage = lazy(() => import('@/features/reports/pages/SparesIssuedReportPage'))
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'))

// ─── Equipment ────────────────────────────────────────────────────────────────
const EquipmentListPage = lazy(() => import('@/features/equipment/pages/EquipmentListPage'))
const EquipmentFormPage = lazy(() => import('@/features/equipment/pages/EquipmentFormPage'))
const EquipmentDetailPage = lazy(() => import('@/features/equipment/pages/EquipmentDetailPage'))
const EquipmentModelsPage = lazy(() => import('@/features/equipment/pages/EquipmentModelsPage'))
const AreasPage = lazy(() => import('@/features/equipment/pages/AreasPage'))
const CategoriesPage = lazy(() => import('@/features/equipment/pages/CategoriesPage'))
const MetricsEntryPage = lazy(() => import('@/features/equipment/pages/MetricsEntryPage'))

// ─── Admin ────────────────────────────────────────────────────────────────────
const UserManagementPage = lazy(() => import('@/features/admin/pages/UserManagementPage'))

// ─── Helpers ──────────────────────────────────────────────────────────────────

function withSuspense(Component: React.ComponentType) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  )
}

// ─── Router ───────────────────────────────────────────────────────────────────

// ─── Vendor Portal (public) ───────────────────────────────────────────────────
const VendorQuotePage = lazy(() => import('@/features/vendor-portal/pages/VendorQuotePage'))
const QRScanPage = lazy(() => import('@/features/equipment/pages/QRScanPage'))

export const router = createBrowserRouter([
  // ── Public routes (no auth) ─────────────────────────────────────────────────
  {
    path: '/vendor-quote/:token',
    element: withSuspense(VendorQuotePage),
  },
  {
    path: '/equipment/scan/:id',
    element: withSuspense(QRScanPage),
  },

  // ── Guest routes (unauthenticated only) ─────────────────────────────────────
  {
    element: <GuestRoute />,
    children: [
      {
        path: '/login',
        element: withSuspense(LoginPage),
      },
      {
        path: '/forgot-password',
        element: withSuspense(ForgotPasswordPage),
      },
    ],
  },

  // ── Protected routes (authenticated only) ───────────────────────────────────
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AppLayout />
          </Suspense>
        ),
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },

          // Dashboard
          { path: 'dashboard', element: withSuspense(DashboardPage) },

          // Maintenance
          { path: 'maintenance', element: <Navigate to="/maintenance/work-orders" replace /> },
          { path: 'maintenance/work-orders', element: withSuspense(WorkOrderListPage) },
          { path: 'maintenance/work-orders/new', element: withSuspense(WorkOrderFormPage) },
          { path: 'maintenance/work-orders/:id', element: withSuspense(WorkOrderDetailPage) },
          { path: 'maintenance/work-orders/:id/edit', element: withSuspense(WorkOrderFormPage) },
          { path: 'maintenance/pm-plans', element: withSuspense(PMPlansPage) },
          { path: 'maintenance/pm-plans/new', element: withSuspense(PMPlanFormPage) },
          { path: 'maintenance/pm-plans/:id', element: withSuspense(PMPlanDetailPage) },
          { path: 'maintenance/pm-plans/:id/edit', element: withSuspense(PMPlanFormPage) },
          { path: 'maintenance/calendar', element: withSuspense(CalendarPage) },
          { path: 'maintenance/requests', element: withSuspense(MaintenanceRequestQueuePage) },

          // Inventory
          { path: 'inventory', element: withSuspense(InventoryDashboardPage) },
          { path: 'inventory/items', element: withSuspense(ItemsListPage) },
          { path: 'inventory/items/new', element: withSuspense(ItemFormPage) },
          { path: 'inventory/items/:id', element: withSuspense(ItemDetailPage) },
          { path: 'inventory/items/:id/edit', element: withSuspense(ItemFormPage) },
          { path: 'inventory/stock', element: withSuspense(StockPage) },
          { path: 'inventory/stock/movement', element: withSuspense(StockMovementPage) },
          { path: 'inventory/locations', element: withSuspense(LocationsPage) },
          { path: 'inventory/issue', element: withSuspense(SpareIssuePage) },

          // Purchase Orders
          { path: 'purchase-orders', element: withSuspense(POListPage) },
          { path: 'purchase-orders/requests', element: withSuspense(RequestQueuePage) },
          { path: 'purchase-orders/rfqs', element: withSuspense(RFQListPage) },
          { path: 'purchase-orders/new', element: withSuspense(POFormPage) },
          { path: 'purchase-orders/:id', element: withSuspense(PODetailPage) },
          { path: 'purchase-orders/:id/edit', element: withSuspense(POFormPage) },

          // Vendors
          { path: 'vendors', element: withSuspense(VendorListPage) },
          { path: 'vendors/new', element: withSuspense(VendorFormPage) },
          { path: 'vendors/:id', element: withSuspense(VendorDetailPage) },
          { path: 'vendors/:id/edit', element: withSuspense(VendorFormPage) },

          // Reports & Settings
          { path: 'reports', element: withSuspense(ReportsPage) },
          { path: 'reports/equipment-status', element: withSuspense(EquipmentStatusReportPage) },
          { path: 'reports/maintenance-cost', element: withSuspense(MaintenanceCostReportPage) },
          { path: 'reports/work-order-summary', element: withSuspense(WorkOrderSummaryPage) },
          { path: 'reports/purchase-cost', element: withSuspense(PurchaseCostSummaryPage) },
          { path: 'reports/spares-issued', element: withSuspense(SparesIssuedReportPage) },
          { path: 'settings', element: withSuspense(SettingsPage) },

          // Equipment
          { path: 'equipment', element: withSuspense(EquipmentListPage) },
          { path: 'equipment/new', element: withSuspense(EquipmentFormPage) },
          { path: 'equipment/models', element: withSuspense(EquipmentModelsPage) },
          { path: 'equipment/areas', element: withSuspense(AreasPage) },
          { path: 'equipment/categories', element: withSuspense(CategoriesPage) },
          { path: 'equipment/:id', element: withSuspense(EquipmentDetailPage) },
          { path: 'equipment/:id/edit', element: withSuspense(EquipmentFormPage) },
          { path: 'equipment/:id/metrics', element: withSuspense(MetricsEntryPage) },

          // Admin
          { path: 'admin/users', element: withSuspense(UserManagementPage) },

          // 404
          { path: '*', element: <Navigate to="/dashboard" replace /> },
        ],
      },
    ],
  },

  // ── Catch-all — redirect to login ──────────────────────────────────────────
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
])
